import React, { useState } from 'react';

/**
 * Careers page component
 * Displays current job openings and company culture information
 * 
 * @returns {JSX.Element} The rendered Careers page
 */
const CareersPage: React.FC = () => {
  try {
    // Log component rendering
    console.log('Rendering Careers page');

    // State for filtered job listings
    const [selectedDepartment, setSelectedDepartment] = useState<string>('All');
    
    // Available departments for filtering
    const departments = ['All', 'Engineering', 'Product', 'Security', 'Marketing', 'Operations'];
    
    // Job listings data
    const jobListings = [
      {
        id: 1,
        title: 'Senior AI Security Engineer',
        department: 'Engineering',
        location: 'San Francisco, CA (Remote Available)',
        type: 'Full-time',
        description: 'Design and implement security measures for AI systems, focusing on vulnerability detection and mitigation.',
      },
      {
        id: 2,
        title: 'Product Manager, Enterprise Solutions',
        department: 'Product',
        location: 'New York, NY (Remote Available)',
        type: 'Full-time',
        description: 'Lead the development of our enterprise AI governance products, working closely with customers and engineering teams.',
      },
      {
        id: 3,
        title: 'AI Compliance Specialist',
        department: 'Security',
        location: 'Remote',
        type: 'Full-time',
        description: 'Ensure our products meet regulatory requirements and help customers navigate the complex landscape of AI compliance.',
      },
      {
        id: 4,
        title: 'Senior Frontend Engineer',
        department: 'Engineering',
        location: 'San Francisco, CA (Remote Available)',
        type: 'Full-time',
        description: 'Build intuitive and responsive interfaces for our AI control plane, using modern web technologies.',
      },
      {
        id: 5,
        title: 'Content Marketing Manager',
        department: 'Marketing',
        location: 'Remote',
        type: 'Full-time',
        description: 'Create compelling content that educates the market about AI security and governance challenges and solutions.',
      },
      {
        id: 6,
        title: 'Customer Success Manager',
        department: 'Operations',
        location: 'Remote',
        type: 'Full-time',
        description: 'Ensure our enterprise customers successfully implement and get maximum value from our AI security platform.',
      },
    ];
    
    // Filter job listings based on selected department
    const filteredJobs = selectedDepartment === 'All' 
      ? jobListings 
      : jobListings.filter(job => job.department === selectedDepartment);
    
    // Handle department filter change
    const handleDepartmentChange = (dept: string) => {
      try {
        console.log(`Filtering jobs by department: ${dept}`);
        setSelectedDepartment(dept);
      } catch (error) {
        console.error('Error filtering jobs by department:', error);
      }
    };

    return (
      <div className="bg-white">
        {/* Hero section */}
        <div className="relative overflow-hidden">
          {/* Background pattern */}
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-900 to-indigo-700 opacity-90"></div>
          <div className="absolute inset-0 bg-[url('/patterns/grid-pattern.svg')] opacity-10"></div>
          
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 -mt-20 -mr-20 w-80 h-80 bg-indigo-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
          <div className="absolute bottom-0 left-0 -mb-20 -ml-20 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
          
          <div className="relative mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:flex lg:items-center lg:gap-x-10 lg:px-8 lg:py-40">
            <div className="lg:w-1/2">
              <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl">
                <span className="block">Join Our Team of</span>
                <span className="block mt-2 bg-gradient-to-r from-purple-200 to-indigo-100 bg-clip-text text-transparent">AI Security Pioneers</span>
              </h1>
              <p className="mt-6 text-lg leading-8 text-indigo-100 max-w-3xl">
                At Raidu, we're building the future of enterprise AI security. We're looking for exceptional talent who are 
                passionate about creating technology that enables organizations to use AI safely, securely, and responsibly.
              </p>
              <div className="mt-10 flex items-center gap-x-6">
                <a 
                  href="#open-positions" 
                  className="rounded-md bg-white px-6 py-3 text-base font-semibold text-indigo-700 shadow-sm hover:bg-indigo-50 transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-indigo-600"
                  onClick={() => {
                    try {
                      console.log('View open positions button clicked');
                    } catch (error) {
                      console.error('Error handling view positions click:', error);
                    }
                  }}
                >
                  View open positions
                </a>
              </div>
            </div>
            <div className="mt-16 sm:mt-24 lg:mt-0 lg:w-1/2 lg:flex-shrink-0 lg:flex lg:justify-end">
              <div className="relative w-full max-w-lg lg:max-w-none">
                <img
                  className="w-full rounded-xl shadow-xl ring-1 ring-white/10"
                  src="/images/team-collaboration.jpg"
                  alt="Raidu team collaboration"
                  onError={(e) => {
                    try {
                      console.error('Error loading team image', e.currentTarget.src);
                      e.currentTarget.src = 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80';
                    } catch (error) {
                      console.error('Error in image error handling:', error);
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Culture section with image */}
        <div className="relative overflow-hidden bg-white py-24 sm:py-32">
          {/* Background subtle pattern */}
          <div className="absolute inset-0 bg-[url('/patterns/dot-pattern.svg')] opacity-5"></div>
          
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="mx-auto max-w-2xl lg:text-center">
              <h2 className="text-lg font-semibold text-indigo-600">Our Culture</h2>
              <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">Where talent meets purpose</p>
            </div>
            
            {/* Image and text section */}
            <div className="mt-16 lg:mt-20">
              <div className="relative overflow-hidden rounded-3xl bg-gray-900 px-6 py-20 shadow-xl sm:px-12 sm:py-28 lg:grid lg:grid-cols-2 lg:gap-x-8 lg:px-16">
                <div className="absolute inset-0 opacity-50 mix-blend-multiply saturate-0 filter">
                  <img
                    src="/images/team-meeting.jpg"
                    alt="Raidu team meeting"
                    className="h-full w-full object-cover"
                    onError={(e) => {
                      try {
                        console.error('Error loading culture image', e.currentTarget.src);
                        e.currentTarget.src = 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2830&q=80';
                      } catch (error) {
                        console.error('Error in image error handling:', error);
                      }
                    }}
                  />
                </div>
                <div className="relative lg:col-span-1">
                  <div className="text-base leading-7 text-gray-300">
                    <h3 className="text-3xl font-bold tracking-tight text-white">Building a world-class team</h3>
                    <p className="mt-6 text-lg">
                      At Raidu, we're committed to creating an environment where exceptional talent can thrive, innovate, and make a meaningful impact on the future of AI security.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Values grid - more visual, less text */}
            <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
              <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-3">
                <div className="flex flex-col">
                  <dt className="flex items-center gap-x-3 text-xl font-semibold leading-7 text-gray-900">
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-indigo-600">
                      <svg className="h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    Innovation-Driven
                  </dt>
                  <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-gray-600">
                    <p className="flex-auto">Creative thinking that pushes the boundaries of AI security.</p>
                  </dd>
                </div>
                
                <div className="flex flex-col">
                  <dt className="flex items-center gap-x-3 text-xl font-semibold leading-7 text-gray-900">
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-indigo-600">
                      <svg className="h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                    </div>
                    Collaborative Environment
                  </dt>
                  <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-gray-600">
                    <p className="flex-auto">Diverse perspectives working together toward common goals.</p>
                  </dd>
                </div>
                
                <div className="flex flex-col">
                  <dt className="flex items-center gap-x-3 text-xl font-semibold leading-7 text-gray-900">
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-indigo-600">
                      <svg className="h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                      </svg>
                    </div>
                    Continuous Learning
                  </dt>
                  <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-gray-600">
                    <p className="flex-auto">Growth through mentorship and cutting-edge technology.</p>
                  </dd>
                </div>
                
                <div className="flex flex-col">
                  <dt className="flex items-center gap-x-3 text-xl font-semibold leading-7 text-gray-900">
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-indigo-600">
                      <svg className="h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    Work-Life Balance
                  </dt>
                  <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-gray-600">
                    <p className="flex-auto">Flexible policies supporting professional growth and personal wellbeing.</p>
                  </dd>
                </div>
                
                <div className="flex flex-col">
                  <dt className="flex items-center gap-x-3 text-xl font-semibold leading-7 text-gray-900">
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-indigo-600">
                      <svg className="h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                      </svg>
                    </div>
                    Impact-Focused
                  </dt>
                  <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-gray-600">
                    <p className="flex-auto">Making AI safer and more secure for enterprise organizations.</p>
                  </dd>
                </div>
                
                <div className="flex flex-col">
                  <dt className="flex items-center gap-x-3 text-xl font-semibold leading-7 text-gray-900">
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-indigo-600">
                      <svg className="h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                      </svg>
                    </div>
                    Remote-Friendly
                  </dt>
                  <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-gray-600">
                    <p className="flex-auto">Global team collaborating effectively across time zones and locations.</p>
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </div>

        {/* Open positions section */}
        <div id="open-positions" className="bg-gray-50 py-24 sm:py-32">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="mx-auto max-w-2xl lg:mx-0">
              <h2 className="text-lg font-semibold text-indigo-600">Careers</h2>
              <h3 className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">Open Positions</h3>
              <p className="mt-6 text-lg leading-8 text-gray-600">
                Join our team of experts working to secure the future of enterprise AI. Explore our current openings below.
              </p>
            </div>
            
            {/* Department filter */}
            <div className="mt-10 flex flex-wrap gap-3">
              {departments.map((dept) => (
                <button
                  key={dept}
                  className={`px-5 py-2.5 rounded-md text-sm font-semibold transition-all duration-200 ${
                    selectedDepartment === dept
                      ? 'bg-indigo-600 text-white shadow-md'
                      : 'bg-white text-gray-700 hover:bg-gray-50 hover:text-indigo-600 border border-gray-200'
                  }`}
                  onClick={() => handleDepartmentChange(dept)}
                >
                  {dept}
                </button>
              ))}
            </div>
            
            {/* Job listings */}
            <div className="mt-10 space-y-6">
              {filteredJobs.length > 0 ? (
                filteredJobs.map((job) => (
                  <div
                    key={job.id}
                    className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
                  >
                    <div className="p-6 sm:p-8">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                        <div>
                          <h3 className="text-xl font-bold text-gray-900">{job.title}</h3>
                          <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-gray-500">
                            <div className="flex items-center">
                              <svg className="h-5 w-5 mr-1.5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                              </svg>
                              {job.location}
                            </div>
                            <div className="flex items-center">
                              <svg className="h-5 w-5 mr-1.5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                              </svg>
                              {job.type}
                            </div>
                          </div>
                        </div>
                        <span className="inline-flex items-center rounded-md bg-indigo-50 px-2.5 py-1 text-sm font-medium text-indigo-700">
                          {job.department}
                        </span>
                      </div>
                      
                      <p className="mt-4 text-gray-600">{job.description}</p>
                      
                      <div className="mt-6">
                        <a
                          href={`mailto:founder@raidu.com?subject=Application for ${job.title} position&body=I am interested in applying for the ${job.title} position at Raidu.%0D%0A%0D%0APlease find my resume attached.%0D%0A%0D%0AThank you,%0D%0A[Your Name]`}
                          className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                          onClick={() => {
                            try {
                              console.log(`User clicked Apply for ${job.title}`);
                            } catch (error) {
                              console.error(`Error handling Apply click for ${job.title}:`, error);
                            }
                          }}
                        >
                          Apply for this position
                          <svg className="ml-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </a>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="bg-white p-8 rounded-lg text-center">
                  <p className="text-gray-600">No positions available in this department at the moment.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  } catch (error) {
    // Log any errors during rendering
    console.error('Error rendering Careers page:', error);
    
    // Return error state
    return (
      <div className="py-12 px-6 bg-red-50 rounded-lg mx-auto max-w-7xl">
        <h2 className="text-2xl font-bold text-red-700 mb-4">Error Loading Careers Page</h2>
        <p className="text-red-600">
          We encountered an issue while loading this page. Please try refreshing or contact support.
        </p>
      </div>
    );
  }
};

export default CareersPage;
